import _ from 'lodash';
import { SimpleUIService } from '../services/simple-ui/simple-ui.service';

/** A project constant entry from the project-version `defaultState`. */
export interface ConstantEntry {
  /** Current schema spelling of the variable name (wins over `name`). */
  key?: string;
  /** Legacy spelling used by the ehq-web project-constants UI. */
  name?: string;
  value?: unknown;
}

/** A workflow variable entry from NotebookVariablesService.variableList. */
export interface WorkflowVariableEntry {
  name?: string;
  value?: unknown;
  sortOrder?: number;
}

export interface BuildStepDataOptions {
  /**
   * Always include a hand-set `job_arguments` variable regardless of
   * sortOrder (Python path behavior since SCRUM-500; the JS path resolves job
   * args separately via getJobArguments). Deprecated with that variable
   * (EHQ-388): variable_schema run inputs reach steps only through
   * getJobArguments(), as in enoch, never through `data`.
   */
  alwaysIncludeJobArgs?: boolean;
}

/**
 * Builds the step-execution `data` map from project constants and workflow
 * variables (SCRUM-626). Mirrors enoch's headless merge
 * (`ehq_core::utils::default_state::merge_default_state_with_constants`):
 *
 * ```text
 * project constant  <  workflow variable
 * ```
 *
 * - Constant names accept both `key` and `name` spellings (`key` wins
 *   within an entry); nameless constant entries are skipped.
 * - A constant shadowed by ANY workflow variable name is dropped entirely —
 *   in enoch's merged default_state a shadowed constant never exists — even
 *   when the variable itself is excluded by sortOrder gating. Each one is
 *   reported with console.warn, worded as enoch's job runner words it.
 * - Workflow variables keep the pre-existing inclusion rule: included once
 *   the step's sortOrder has reached them.
 * - With no (or malformed) constants the result is identical to the
 *   variables-only build.
 */
export class StepDataHelper {
  /** Variable name of a constant entry (`key` wins over legacy `name`). */
  static constantName(entry: ConstantEntry | null | undefined): string | undefined {
    const name = entry?.key ?? entry?.name;
    return typeof name === 'string' && name.length > 0 ? name : undefined;
  }

  static buildStepDataMap(
    constants: ConstantEntry[] | null | undefined,
    variables: WorkflowVariableEntry[] | null | undefined,
    chunkSortOrder: number | null,
    opts: BuildStepDataOptions = {},
  ): Record<string, unknown> {
    const data: Record<string, unknown> = {};
    const variableList = Array.isArray(variables) ? variables : [];

    // Shadow set: ALL workflow variable names, regardless of sortOrder.
    const variableNames = new Set(
      variableList
        .map((v) => v.name)
        .filter((n): n is string => typeof n === 'string'),
    );

    // Lay down unshadowed constants first…
    if (Array.isArray(constants)) {
      for (const entry of constants) {
        const name = StepDataHelper.constantName(entry);
        if (!name) {
          continue;
        }
        if (variableNames.has(name)) {
          // Same wording as enoch's job runner (execution.rs), so a shadowed
          // constant is just as visible in the browser console as in a job log.
          console.warn(
            `Workflow default_state key '${name}' shadows a project constant (workflow value wins)`,
          );
          continue;
        }
        data[name] = entry.value;
      }
    }

    // …then overlay workflow variables (workflow wins collisions).
    for (const element of variableList) {
      if (typeof element.name !== 'string') {
        continue;
      }
      const isJobArgsVar =
        !!opts.alwaysIncludeJobArgs &&
        SimpleUIService.isJobArgumentsVariableName(element.name);
      const reached =
        chunkSortOrder != null &&
        element.sortOrder != null &&
        chunkSortOrder >= element.sortOrder;
      if (isJobArgsVar || reached) {
        data[element.name] = element.value;
      }
    }

    return data;
  }

  /**
   * The inverse of buildStepDataMap, for write-back: drop what a step's `data`
   * holds only because a project constant was laid into it.
   *
   * buildStepDataMap puts unshadowed constants into `data`, and every runtime
   * writes the whole of `data` back as workflow variables after the step. Without
   * this, the first step that runs turns every project constant into a workflow
   * variable, which then shadows the constant from then on, so a later change to
   * the constant is silently ignored in that workflow.
   *
   * A key is dropped only when all three hold:
   * - it names a project constant,
   * - no workflow variable of that name exists (if one does, the value came from
   *   the variable, and a write to it is an ordinary variable update), and
   * - the value still equals the constant's (a step that assigned a new value to
   *   a constant's name meant to, and that becomes a workflow variable, as it
   *   would in enoch's single data bag).
   */
  static withoutUnchangedConstants(
    data: Record<string, unknown>,
    constants: ConstantEntry[] | null | undefined,
    variables: WorkflowVariableEntry[] | null | undefined,
  ): Record<string, unknown> {
    if (!Array.isArray(constants) || constants.length === 0) {
      return data;
    }
    const variableNames = new Set(
      (Array.isArray(variables) ? variables : [])
        .map((v) => v.name)
        .filter((n): n is string => typeof n === 'string'),
    );
    const constantValues = new Map<string, unknown>();
    for (const entry of constants) {
      const name = StepDataHelper.constantName(entry);
      if (name) constantValues.set(name, entry.value);
    }

    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data)) {
      const isUnchangedConstant =
        constantValues.has(key) &&
        !variableNames.has(key) &&
        _.isEqual(value, constantValues.get(key));
      if (!isUnchangedConstant) {
        result[key] = value;
      }
    }
    return result;
  }
}
